import { useState, useEffect } from 'react'
import { styled, useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import ConfirmDialog from "../../../../Shared/Components/ConfirmDialog";
import {TicketAttachment} from "../../../../Shared/Types/Ticket";
import {
    FindTicketAttachments,
    SendTicketAttachment,
    RemoveTicketAttachment,
    DownloadTicketAttachment
} from "../TicketReactQuery";

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

type TicketAttachmentsProps = {
    ticketId?: number;
    disabled: boolean;
};

const TicketAttachments = ({ticketId, disabled}: TicketAttachmentsProps) => {
    const theme = useTheme();
    const [attachments, setAttachments] = useState<Array<TicketAttachment>>([]);
    const {data: findTicketAttachments} = FindTicketAttachments(ticketId);
    const [selectedToDelete, setSelectedToDelete] = useState<TicketAttachment | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const mutationSend = SendTicketAttachment(ticketId);
    const mutationRemove = RemoveTicketAttachment(ticketId);

    const onChangeUpload = (file: File) => {
        // FormData é necessário para enviar dados multi-part (arquivos)
        const formData = new FormData();
        formData.append('file', file);

        // Reliza o envio do arquivo
        const handleSuccess = (res: any) => {
            setAttachments([
                ...attachments,
                res.data
            ]);
        };
        mutationSend.mutate(formData, { onSuccess: handleSuccess });
    };

    const onClickDownload = (ticketAttachment: TicketAttachment) => {
        DownloadTicketAttachment(ticketAttachment);
    };

    const onClickDelete = (ticketAttachment: TicketAttachment) => {
        setSelectedToDelete(ticketAttachment);
        setOpenDialog(!openDialog);
    };

    const handleDialogConfirm = async () => {
        if (ticketId && selectedToDelete) {
            const handleSuccess = (res: any) => {
                setAttachments(attachments.filter((item) => item.id !== selectedToDelete.id));
                setOpenDialog(!openDialog);
                setSelectedToDelete(null);
            };
            mutationRemove.mutate(selectedToDelete.id, { onSuccess: handleSuccess });
        }
    };

    useEffect(() => {
        setAttachments(findTicketAttachments?.data || []);
    }, [findTicketAttachments?.data]);

    return (
        <>
        <Card sx={{
           borderRadius: 4,
            boxShadow: 2,
            borderStyle: "solid",
            borderWidth: 1,
            borderColor: theme.palette.primary.main,
        }}>
            <CardHeader
                sx={{ paddingBottom: 0 }}
                title={
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                        <Typography sx={{
                                fontSize: 14,
                                marginRight: 2,
                                marginBottom: 0,
                            }}
                            color="text.secondary"
                            gutterBottom
                        >
                            ANEXOS
                        </Typography>

                        {/* Botão para selecionar anexo */}
                        <Button
                            variant="contained"
                            color="primary"
                            size={"small"}
                            component="label"
                            role={undefined}
                            tabIndex={-1}
                            endIcon={<CloudUploadIcon />}
                            disabled={disabled}
                        >
                            Upload File
                            <VisuallyHiddenInput
                                type="file"
                                onChange={(event) => event.target.files ? onChangeUpload(event.target.files[0]) : null}
                            />
                        </Button>
                    </Box>
                }
            />
            <CardContent sx={{
                paddingTop: 0
            }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    margin: "auto",
                  }}
                >
                  {/* Área de anexos */}
                  <Paper
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      minHeight: "180px",
                      maxHeight: "180px",
                      boxShadow: 'none',
                    }}
                  >
                    <List>
                      {attachments.map((attachment, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primaryTypographyProps={{ fontSize:'0.95em' }}
                            primary={attachment.fileName}
                          />
                          <IconButton
                            aria-label="download"
                            size={"small"}
                            onClick={() => onClickDownload(attachment)}
                          >
                            <DownloadIcon />
                          </IconButton>
                          <IconButton
                            aria-label="delete"
                            size={"small"}
                            onClick={() => onClickDelete(attachment)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Box>
            </CardContent>
        </Card>
        <ConfirmDialog
            title={'Remover'}
            description={`Deseja remover o anexo: ${selectedToDelete?.fileName}?`}
            open={openDialog}
            handleConfirm={handleDialogConfirm}
            handleClose={() => setOpenDialog(!openDialog)}
        />
        </>
    );
}

export default TicketAttachments;